/**
 * PostHog singleton for apps/question-editor.
 *
 * When VITE_POSTHOG_KEY is unset (local dev without credentials), this module
 * exports a frozen no-op stub that satisfies the @posthog/react hook surface
 * without any network traffic.
 *
 * When the key is set, posthog-js is initialised in-place (it is a global
 * singleton inside the posthog-js package) and exported as the live instance.
 *
 * Usage:
 *   // main.tsx
 *   import { initPostHog, posthog } from './lib/posthog';
 *   import { PostHogProvider } from '@posthog/react';
 *   initPostHog();
 *   // ...render <PostHogProvider client={posthog as any}>...</PostHogProvider>
 *
 *   // auth.ts, etc.
 *   import { posthog } from './lib/posthog';
 *   posthog.identify(user.id); // no-op stub or live call
 */

import posthogJs from 'posthog-js';
import { getCachedUser } from './auth';

// ---------------------------------------------------------------------------
// No-op stub — used when VITE_POSTHOG_KEY is not configured.
// Satisfies the subset of the PostHog client that @posthog/react hooks read:
//   · isFeatureEnabled — called in useFeatureFlagEnabled useState initializer
//   · onFeatureFlags   — called in useFeatureFlagEnabled useEffect; must
//                        return a cleanup function (the unsubscribe)
//   · featureFlags.hasLoadedFlags — checked before falling back to bootstrap;
//                        true so the hook returns isFeatureEnabled() directly
// Returns `undefined` for every flag → per the A2 convention, gated tabs hide.
// This is the safe-failure mode for a prod build where the key got dropped.
// ---------------------------------------------------------------------------
export const noopPostHog = Object.freeze({
  isFeatureEnabled: (_flag: string): boolean | undefined => undefined,
  onFeatureFlags:
    (_cb: () => void): (() => void) =>
    () => {},
  featureFlags: { hasLoadedFlags: true as const },
  identify: (_distinctId: string): void => {},
  reset: (): void => {},
  capture: (_event: string): void => {},
});

// ---------------------------------------------------------------------------
// Local-dev stub — used when running under Vite dev server (`pnpm dev`) or
// against a localhost hostname. Skips posthog-js init entirely so zero network
// calls go to PostHog Cloud (no /flags, no /array/{key}/config.js, no
// /capture) — keeps dev sessions out of the Persons dashboard and avoids any
// dependency on the cloud being reachable from the developer's network.
// Returns `true` for every flag so gated tabs ARE visible during development
// without joining the prod allowlist.
// ---------------------------------------------------------------------------
export const localDevPostHog = Object.freeze({
  isFeatureEnabled: (_flag: string): boolean | undefined => true,
  onFeatureFlags: (cb: () => void): (() => void) => {
    cb();
    return () => {};
  },
  featureFlags: { hasLoadedFlags: true as const },
  identify: (_distinctId: string): void => {},
  reset: (): void => {},
  capture: (_event: string): void => {},
});

/**
 * Detects whether we're running under Vite dev or against a localhost
 * hostname. True in either case. Production Cloudflare Pages builds and
 * any non-localhost hostname return false.
 */
export function isLocalDev(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
}

// The single exported instance. Points to the stub until initPostHog() is
// called with a valid key, after which it references the live posthog-js
// instance (which has been init'd in-place).
// Exported as `let` so main.tsx can reassign after initPostHog() if needed;
// but auth.ts and other callers always import this binding and see whatever
// it currently references.
export let posthog: typeof posthogJs | typeof noopPostHog = noopPostHog;

/**
 * Initialise PostHog once. Call before createRoot().render() in main.tsx.
 * Returns the instance (live posthog-js or stub) for passing to
 * PostHogProvider's `client` prop.
 *
 * Safe to call unconditionally — no-ops when VITE_POSTHOG_KEY is empty.
 */
export function initPostHog(): typeof posthogJs | typeof noopPostHog | typeof localDevPostHog {
  // Localhost / Vite dev → skip posthog-js init entirely, return local-dev
  // stub so flagged tabs are visible during development without network.
  if (isLocalDev()) {
    posthog = localDevPostHog as unknown as typeof posthogJs;
    return localDevPostHog;
  }

  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    posthog = noopPostHog;
    return noopPostHog;
  }

  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

  // Populate bootstrap.distinctID from the cached user so the instance is
  // identified before the first /flags request — avoids an anonymous event
  // burst on page reload.
  const cachedUser = getCachedUser();
  const bootstrapDistinctID = cachedUser?.id;

  posthogJs.init(key, {
    api_host: host,
    autocapture: false,
    capture_pageview: true,
    persistence: 'localStorage+cookie',
    ...(bootstrapDistinctID ? { bootstrap: { distinctID: bootstrapDistinctID } } : {}),
    loaded: (ph) => {
      // If a cached user exists, identify them — covers page reloads where
      // the HttpOnly cookie silently restores the session before any login.
      if (bootstrapDistinctID) {
        ph.identify(bootstrapDistinctID);
      }
    },
  });

  posthog = posthogJs;
  return posthogJs;
}
